import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonSpinner,
  IonToast,
  IonToolbar
} from '@ionic/react';
import SafeIonPage from '../../components/SafeIonPage';
import {
  AdministrativeSectionKey,
  getAppConfig,
  isAdministrativeSectionEnabled
} from '../../config/app.config';
import {
  AdministrativeSectionDataMap,
  administrativeSectionService
} from '../../services/AdministrativeSectionService';
import './AdministrativeSections.scss';

interface AdministrativeRouteParams {
  section: string;
}

interface SectionDefinition {
  key: AdministrativeSectionKey;
  load: () => Promise<{ default: React.ComponentType<any> }>;
}

const sections: Record<string, SectionDefinition> = {
  membership: { key: 'membership', load: () => import('./sections/MembershipSection') },
  events: { key: 'associationEvents', load: () => import('./sections/AssociationEventsSection') },
  participation: { key: 'participation', load: () => import('./sections/ParticipationSection') },
  account: { key: 'accountStatement', load: () => import('./sections/AccountStatementSection') },
  card: { key: 'digitalCard', load: () => import('./sections/DigitalMemberCardSection') },
  benefits: { key: 'benefits', load: () => import('./sections/BenefitsSection') },
  news: { key: 'news', load: () => import('./sections/NewsSection') },
  certificates: { key: 'certificates', load: () => import('./sections/CertificatesSection') }
};

export interface AdministrativeSectionComponentProps<K extends AdministrativeSectionKey> {
  data: AdministrativeSectionDataMap[K];
  onNavigate: (section: AdministrativeSectionKey) => void;
  onToast: (message: string) => void;
}

const AdministrativeSectionPage: React.FC<RouteComponentProps<AdministrativeRouteParams>> = ({ match, history }) => {
  const definition = useMemo(() => sections[match.params.section], [match.params.section]);
  const [SectionComponent, setSectionComponent] = useState<React.ComponentType<any> | null>(null);
  const [sectionData, setSectionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    setSectionComponent(null);
    setSectionData(null);

    if (!definition || !isAdministrativeSectionEnabled(definition.key)) {
      setLoading(false);
      setError('Esta sección no está habilitada en la configuración de la aplicación.');
      return () => { mounted = false; };
    }

    Promise.all([
      definition.load(),
      administrativeSectionService.loadSection(definition.key)
    ])
      .then(([module, data]) => {
        if (!mounted) return;
        setSectionComponent(() => module.default);
        setSectionData(data);
      })
      .catch(loadError => {
        console.error(`No se pudo cargar la sección ${definition.key}:`, loadError);
        if (mounted) setError('No se pudo cargar esta sección. Intente nuevamente.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [definition]);

  const navigateToSection = (section: AdministrativeSectionKey) => {
    const sectionConfig = getAppConfig().administrative.sections[section];
    if (sectionConfig.enabled) history.push(sectionConfig.route);
  };

  const sectionConfig = definition
    ? getAppConfig().administrative.sections[definition.key]
    : null;

  return (
    <SafeIonPage id="administrative-section-page" className="administrative-section-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <div className="administrative-native-title">
            <strong>{sectionConfig?.label || 'Sección administrativa'}</strong>
            <small>{sectionConfig?.description || 'Módulo no disponible'}</small>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen={true}>
        {loading && <div className="administrative-loading"><IonSpinner name="crescent" /><p>Cargando sección…</p></div>}
        {!loading && error && <div className="administrative-empty"><h2>Sección no disponible</h2><p>{error}</p><IonButton onClick={() => history.push('/tabs/schedule')}>Volver a la app</IonButton></div>}
        {!loading && !error && SectionComponent && sectionData && (
          <SectionComponent
            data={sectionData}
            onNavigate={navigateToSection}
            onToast={setToastMessage}
          />
        )}
      </IonContent>
      <IonToast isOpen={Boolean(toastMessage)} message={toastMessage} duration={2600} onDidDismiss={() => setToastMessage('')} />
    </SafeIonPage>
  );
};

export default AdministrativeSectionPage;
